import { useCmsSectionsQuery } from "@/modules/cms/presentation/api/queries";
import type { CmsContentAboutServicesForm } from "@/modules/cms/domain/contracts";
import { Badge, Label, RHF, cn } from "@devhop/ui";
import { Check } from "lucide-react";

type ShowcaseServiceSlugsFieldProps = {
  name?: string;
  disabled?: boolean;
};

function pickServiceLabel(
  title: { lo?: string; th?: string; en?: string } | undefined,
  slug: string,
): string {
  return title?.lo?.trim() || title?.th?.trim() || title?.en?.trim() || slug;
}

export function ShowcaseServiceSlugsField({
  name = "showcaseServiceSlugs",
  disabled,
}: ShowcaseServiceSlugsFieldProps) {
  const { data: sections, isLoading } = useCmsSectionsQuery("about");

  const servicesSection = sections?.find(
    (s) => s.sectionKey === "about.services",
  );
  const serviceItems =
    (servicesSection?.content as CmsContentAboutServicesForm | undefined)
      ?.items ?? [];
  const activeServices = serviceItems.filter(
    (item) => item.isActive && item.slug?.trim(),
  );

  return (
    <div className="space-y-2" data-tourid="product-form-showcase-services">
      <Label>ບໍລິການ About (ສະແດງໃນ Showcase)</Label>
      {isLoading ? (
        <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດລາຍການບໍລິການ...</p>
      ) : activeServices.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          ຍັງບໍ່ມີບໍລິການ — ສ້າງໃນ CMS ກ່ຽວກັບ → ບໍລິການ
        </p>
      ) : (
        <RHF.Controller
          name={name}
          render={({ field, fieldState }) => (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                {activeServices.map((svc) => {
                  const selected = (field.value as string[] | undefined)?.includes(
                    svc.slug,
                  );
                  return (
                    <label
                      key={svc.id}
                      className="cursor-pointer"
                      htmlFor={`showcase-svc-${svc.slug}`}
                    >
                      <input
                        id={`showcase-svc-${svc.slug}`}
                        type="checkbox"
                        className="peer sr-only"
                        checked={!!selected}
                        disabled={disabled}
                        onChange={() => {
                          const current = (field.value as string[]) ?? [];
                          const next = selected
                            ? current.filter((s) => s !== svc.slug)
                            : [...current, svc.slug];
                          field.onChange(next);
                        }}
                      />
                      <Badge
                        variant={selected ? "secondary" : "outline"}
                        className={cn(
                          "inline-flex cursor-pointer items-center gap-1.5 px-3 py-1.5 transition-colors",
                          "hover:bg-secondary/80",
                          selected && "border-primary bg-primary/10 text-primary",
                        )}
                      >
                        {selected && (
                          <Check className="h-3.5 w-3.5 shrink-0" />
                        )}
                        {pickServiceLabel(svc.title, svc.slug)}
                      </Badge>
                    </label>
                  );
                })}
              </div>
              {fieldState.error?.message && (
                <p className="text-destructive text-sm">
                  {String(fieldState.error.message)}
                </p>
              )}
            </div>
          )}
        />
      )}
      <p className="text-muted-foreground text-xs">
        ເລືອກຢ່າງໜ້ອຍ 1 ບໍລິການ — ແບບນີ້ຈະໂຜລ່ໃນ Showcase ຂອງບໍລິການນັ້ນເທົ່ານັ້ນ
      </p>
    </div>
  );
}
